/**
 * Parse `[label](url)` markdown-style links out of bot LLM output so the
 * MessageBubble can render them as clickable elements. Used together with
 * `parseInlineRefs` — call this on the `text` segments it produces.
 *
 * Token format: [label](url)
 *   - label: any text without an unescaped `]`
 *   - url:   any text without an unescaped `)` (the relative checkout path
 *            `/shopping-cart?bot_session_id=…` is the common case today;
 *            absolute http(s) URLs work too)
 *
 * Malformed tokens (empty label, empty url) fall through as plain text so
 * we never crash on weird LLM output.
 */
export type MarkdownLinkSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; label: string; url: string };

const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

export function parseMarkdownLinks(text: string): MarkdownLinkSegment[] {
  if (!text) return [{ type: 'text', value: '' }];

  const out: MarkdownLinkSegment[] = [];
  let cursor = 0;

  const matches = Array.from(text.matchAll(LINK_RE));

  for (const match of matches) {
    const [full, rawLabel, rawUrl] = match;
    const idx = match.index ?? 0;
    const label = (rawLabel || '').trim();
    const url = (rawUrl || '').trim();

    if (!label || !url) continue;

    if (idx > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, idx) });
    }
    out.push({ type: 'link', label, url });
    cursor = idx + full.length;
  }

  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) });
  }
  return out.length ? out : [{ type: 'text', value: text }];
}
